/* eslint-disable no-console */
/**
 * @fileoverview Hook of Data Layer for centralized and deduplicated management of WebSocket events in the Inbox.
 * Allows handling real-time messaging and customer events with deduplication and listener control.
 * Meets the standards of the architecture and documentation of Mercately (v3.2.0).
 * @module useInboxSocketEvents
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 3.2.1 - Critical optimizations of remount and listener stability
 */

import {
  useEffect,
  useCallback,
  useRef,
  useMemo
} from 'react';
import { isInboxSocketDebugEnabled } from '../../../utils/inboxSocketDebug';

const DEFAULT_ENABLED_EVENTS = Object.freeze([
  'new_message',
  'customer_update',
  'message_update',
  'chat_counter_update'
]);

/**
 * Hook of Data Layer for centralized and deduplicated management of WebSocket events in the Inbox.
 *
 * Implements the **Data Layer** pattern with strict separation of responsibilities,
 * focusing on subscription, deduplication and listener control for real-time messaging and customer events.
 */
const useInboxSocketEvents = (options = {}) => {
  const {
    enabledEvents: providedEnabledEvents,
    onNewMessage,
    onCustomerUpdate,
    onMessageUpdate,
    onChatCounterUpdate,
    socketInstance,
    getSocket,
    socketUrl,
    socketOptions,
    socketInstances,
    getSockets,
    socketUrls,
    socketOptionsList,
    roomId,
    additionalRoomIds
  } = options;

  // Stable key: same logical list even if the parent passes a new array each render
  const enabledEventsKey = providedEnabledEvents?.length > 0
    ? [...providedEnabledEvents].sort().join(',')
    : 'default';

  const enabledEvents = useMemo(() => {
    if (enabledEventsKey === 'default') {
      return DEFAULT_ENABLED_EVENTS;
    }
    return [...providedEnabledEvents];
  // enabledEventsKey resumes the content; including providedEnabledEvents would re-trigger on each new reference from the parent
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledEventsKey]);

  const callbacksRef = useRef({
    onNewMessage,
    onCustomerUpdate,
    onMessageUpdate,
    onChatCounterUpdate
  });
  callbacksRef.current = {
    onNewMessage,
    onCustomerUpdate,
    onMessageUpdate,
    onChatCounterUpdate
  };

  const listenersRef = useRef({});
  const activeSocketsRef = useRef([]);
  const customSocketsByUrlRef = useRef(new Map());
  const roomHandlersRef = useRef([]);

  // Cache for deduplication of exactly equal events only
  const eventCacheRef = useRef(new Map());
  const CACHE_DURATION = 1000; // 1 second to consider exactly duplicate event

  /**
   * Function to deduplicate exactly equal events
   */
  const isDuplicateEvent = useCallback((eventType, data) => {
    const now = Date.now();

    // Create unique event hash based on the type and TODO the content
    // Without extracting specific data - use the complete content as it comes
    const fullContent = JSON.stringify(data);
    const eventHash = `${eventType}_${fullContent}`;

    if (isInboxSocketDebugEnabled()) {
      console.log(`🔍 Verificando duplicado para evento: ${eventType}, hash: ${eventHash}`);
    }

    // Check if it is an event exactly equal to the one we processed recently
    const lastEventTime = eventCacheRef.current.get(eventHash);
    if (isInboxSocketDebugEnabled()) {
      console.log(`🕒 Último tiempo registrado para este hash: ${lastEventTime}`);
      console.log(`⏱️ now: ${now}, lastEventTime: ${lastEventTime}, diff: ${now - lastEventTime}`);
    }

    if (lastEventTime && (now - lastEventTime) < CACHE_DURATION) {
      return true;
    }

    // Register event as processed
    eventCacheRef.current.set(eventHash, now);

    // Clean old events from the cache
    const cutoffTime = now - (CACHE_DURATION * 2);
    Array.from(eventCacheRef.current.entries()).forEach(([hash, time]) => {
      if (time < cutoffTime) {
        eventCacheRef.current.delete(hash);
      }
    });

    return false;
  }, []);

  /**
   * Stable handlers: read callbacks from ref to not re-subscribe the socket on each parent render.
   */
  const handleNewMessage = useCallback((data) => {
    if (isDuplicateEvent('new_message', data)) return;
    const cb = callbacksRef.current.onNewMessage;
    if (typeof cb === 'function') {
      cb(data);
    }
  }, [isDuplicateEvent]);

  /**
   * Handler for 'customer_update' event
   * @param {Object} data - Customer update data
   */
  const handleCustomerUpdate = useCallback((data) => {
    if (isDuplicateEvent('customer_update', data)) return;
    const cb = callbacksRef.current.onCustomerUpdate;
    if (typeof cb === 'function') {
      cb(data);
    }
  }, [isDuplicateEvent]);

  const handleMessageUpdate = useCallback((data) => {
    if (isDuplicateEvent('message_update', data)) return;
    const cb = callbacksRef.current.onMessageUpdate;
    if (typeof cb === 'function') {
      cb(data);
    }
  }, [isDuplicateEvent]);

  const handleChatCounterUpdate = useCallback((data) => {
    if (isDuplicateEvent('chat_counter_update', data)) return;
    const cb = callbacksRef.current.onChatCounterUpdate;
    if (typeof cb === 'function') {
      cb(data);
    }
  }, [isDuplicateEvent]);

  // Memoized function to determine if an event should be enabled
  const shouldEnableEvent = useMemo(
    () => (eventType) => enabledEvents.includes(eventType),
    [enabledEvents]
  );

  const resolveSockets = useCallback(() => {
    const resolvedSockets = [];

    if (Array.isArray(socketInstances) && socketInstances.length > 0) {
      resolvedSockets.push(...socketInstances.filter(Boolean));
    }

    if (typeof getSockets === 'function') {
      const socketsFromGetter = getSockets();
      if (Array.isArray(socketsFromGetter) && socketsFromGetter.length > 0) {
        resolvedSockets.push(...socketsFromGetter.filter(Boolean));
      }
    }

    if (Array.isArray(socketUrls) && socketUrls.length > 0 && typeof window !== 'undefined' && window.io) {
      socketUrls.forEach((url, index) => {
        if (!url) return;
        if (!customSocketsByUrlRef.current.has(url)) {
          const optionsForUrl = Array.isArray(socketOptionsList) ? socketOptionsList[index] : undefined;
          customSocketsByUrlRef.current.set(url, window.io(url, optionsForUrl || {}));
        }
        resolvedSockets.push(customSocketsByUrlRef.current.get(url));
      });
    }

    if (socketInstance) {
      resolvedSockets.push(socketInstance);
    }

    if (typeof getSocket === 'function') {
      const singleSocket = getSocket();
      if (singleSocket) {
        resolvedSockets.push(singleSocket);
      }
    }

    if (socketUrl && typeof window !== 'undefined' && window.io) {
      if (!customSocketsByUrlRef.current.has(socketUrl)) {
        customSocketsByUrlRef.current.set(socketUrl, window.io(socketUrl, socketOptions || {}));
      }
      resolvedSockets.push(customSocketsByUrlRef.current.get(socketUrl));
    }

    return Array.from(new Set(resolvedSockets.filter(Boolean)));
  }, [
    socketInstances,
    getSockets,
    socketUrls,
    socketOptionsList,
    socketInstance,
    getSocket,
    socketUrl,
    socketOptions
  ]);

  const resolveRoomNames = useCallback(() => {
    const roomNames = [];
    const primaryRoomId = roomId;

    if (primaryRoomId) {
      roomNames.push(primaryRoomId);
    }

    const sourceAdditionalRoomIds = Array.isArray(additionalRoomIds)
      ? additionalRoomIds
      : [];

    sourceAdditionalRoomIds.forEach((workspaceId) => {
      if (!workspaceId) return;
      roomNames.push(`other_workspace_${workspaceId}`);
    });

    return Array.from(new Set(roomNames));
  }, [roomId, additionalRoomIds]);

  const getSocketLabel = useCallback((socket, socketIndex) => {
    const socketUrlFromIo = socket?.io?.uri || socket?.io?.opts?.hostname;
    const socketUrlFromNsp = socket?.nsp;
    const socketId = socket?.id || 'pending';
    const socketResolvedUrl = socketUrlFromIo || socketUrlFromNsp || 'unknown-url';
    return `Socket[${socketIndex}] id=${socketId} url=${socketResolvedUrl}`;
  }, []);

  // Main effect to subscribe to events
  useEffect(() => {
    const resolvedSockets = resolveSockets();

    if (resolvedSockets.length === 0) {
      console.warn('⚠️ No sockets resolved for useInboxSocketEvents. Send socketUrls/socketInstances explicitly.');
      return () => {};
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`🔌 useInboxSocketEvents: Resolved sockets count = ${resolvedSockets.length}`);
      resolvedSockets.forEach((socket, socketIndex) => {
        console.log(`🔌 ${getSocketLabel(socket, socketIndex)} connected=${Boolean(socket?.connected)}`);
      });
    }

    activeSocketsRef.current = resolvedSockets;
    const listenersByEvent = {};
    const roomNames = resolveRoomNames();
    const roomHandlers = [];

    // Configure listeners only for enabled events
    const eventHandlers = {
      new_message: handleNewMessage,
      customer_update: handleCustomerUpdate,
      message_update: handleMessageUpdate,
      chat_counter_update: handleChatCounterUpdate
    };

    resolvedSockets.forEach((socket, socketIndex) => {
      if (roomNames.length > 0) {
        const subscribeToRooms = () => {
          if (isInboxSocketDebugEnabled()) {
            console.log(`🏠 ${getSocketLabel(socket, socketIndex)} subscribing to rooms:`, roomNames);
          }
          roomNames.forEach((roomName) => {
            socket.emit('create_room', roomName);
          });
        };

        if (socket.connected) {
          subscribeToRooms();
        }

        socket.on('connect', subscribeToRooms);
        roomHandlers.push({ socket, subscribeToRooms });
      }

      Object.entries(eventHandlers).forEach(([eventType, handler]) => {
        if (shouldEnableEvent(eventType)) {
          socket.on(eventType, handler);
          if (!listenersByEvent[eventType]) {
            listenersByEvent[eventType] = [];
          }
          listenersByEvent[eventType].push({ socket, handler });
          if (isInboxSocketDebugEnabled()) {
            console.log(`🎧 ${getSocketLabel(socket, socketIndex)} subscribed to event: ${eventType}`);
            const finalCount = socket.listeners(eventType).length;
            if (finalCount > 1) {
              console.warn(`⚠️ ${getSocketLabel(socket, socketIndex)} event ${eventType} has ${finalCount} active listeners. This may cause duplicated events.`);
            }
          }
        }
      });
    });

    // Save reference for cleanup
    listenersRef.current = listenersByEvent;
    roomHandlersRef.current = roomHandlers;

    const eventCache = eventCacheRef.current;

    const customSocketsMap = customSocketsByUrlRef.current;

    // Cleanup when unmounting or changing dependencies
    return () => {
      Object.entries(listenersByEvent).forEach(([eventType, listenerRecords]) => {
        listenerRecords.forEach(({ socket, handler }) => {
          socket.off(eventType, handler);
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔇 Unsubscribed from event: ${eventType}`);
          }
        });
      });

      roomHandlers.forEach(({ socket, subscribeToRooms }) => {
        socket.off('connect', subscribeToRooms);
      });

      customSocketsMap.forEach((socket, url) => {
        socket.disconnect();
        customSocketsMap.delete(url);
      });

      listenersRef.current = {};
      roomHandlersRef.current = [];
      eventCache.clear();
      activeSocketsRef.current = [];
    };
  }, [
    handleNewMessage,
    handleCustomerUpdate,
    shouldEnableEvent,
    handleMessageUpdate,
    handleChatCounterUpdate,
    resolveSockets,
    resolveRoomNames,
    getSocketLabel
  ]);

  /**
   * Function to unsubscribe manually from specific events
   */
  const unsubscribeFromEvent = useCallback((eventType) => {
    const listenerRecords = listenersRef.current[eventType];
    if (Array.isArray(listenerRecords) && listenerRecords.length > 0) {
      listenerRecords.forEach(({ socket, handler }) => {
        socket.off(eventType, handler);
      });
      delete listenersRef.current[eventType];
      console.log(`🔇 Manual unsubscription: ${eventType}`);
    }
  }, []);

  /**
   * Function to reactivate specific events that were manually unsubscribed
   */
  const resubscribeToEvent = useCallback((eventType) => {
    if (!shouldEnableEvent(eventType)) return;
    const sockets = activeSocketsRef.current.length > 0 ? activeSocketsRef.current : resolveSockets();
    if (sockets.length === 0) return;
    const eventHandlers = {
      new_message: handleNewMessage,
      customer_update: handleCustomerUpdate,
      message_update: handleMessageUpdate,
      chat_counter_update: handleChatCounterUpdate
    };
    const handler = eventHandlers[eventType];
    if (handler && !listenersRef.current[eventType]) {
      listenersRef.current[eventType] = [];
      sockets.forEach((socket) => {
        socket.on(eventType, handler);
        listenersRef.current[eventType].push({ socket, handler });
      });
      console.log(`🎧 Resuscripción manual: ${eventType}`);
    }
  }, [
    shouldEnableEvent,
    handleNewMessage,
    handleCustomerUpdate,
    handleMessageUpdate,
    handleChatCounterUpdate,
    resolveSockets
  ]);

  /**
   * Function to check the connection state of the socket
   */
  const isSocketConnected = useCallback(
    () => {
      const sockets = activeSocketsRef.current.length > 0 ? activeSocketsRef.current : resolveSockets();
      return sockets.some((socket) => Boolean(socket && socket.connected));
    },
    [resolveSockets]
  );

  // Return state and control functions
  return {
    // State
    isConnected: isSocketConnected(),
    activeListeners: Object.keys(listenersRef.current),

    // Control functions
    unsubscribeFromEvent,
    resubscribeToEvent,

    // Debug information
    enabledEvents,

    // Deduplication information (only for debugging)
    cacheSize: eventCacheRef.current.size,
    getCacheInfo: () => ({
      exactMatches: Array.from(eventCacheRef.current.entries())
    })
  };
};

export default useInboxSocketEvents;
